import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAd } from '../context/AdContext';
import supabase from '../supabase/supabase';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

// Destructure all used icons to ensure they are defined
const { 
  FiShield, FiSettings, FiActivity, FiUsers, FiDollarSign, 
  FiLock, FiUnlock, FiSave, FiRefreshCw, FiArrowLeft, 
  FiLogOut, FiList, FiCheck, FiX, FiClock, FiTrendingUp, FiSearch,
  FiTrash2, FiSlash
} = FiIcons;

const AdminDashboard = () => {
  const { PH_CONFIG, updateConfig, adState, getConversionRate } = useAd();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [localConfig, setLocalConfig] = useState(PH_CONFIG);

  // Data States
  const [withdrawals, setWithdrawals] = useState([]);
  const [loadingWithdrawals, setLoadingWithdrawals] = useState(false);
  const [players, setPlayers] = useState([]); // Renamed from users to players for clarity
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [playerSearch, setPlayerSearch] = useState('');

  const navigate = useNavigate();

  // Admin Credentials (Secure access control)
  const ADMIN_EMAIL = 'admin@admin.com';
  const ADMIN_PASS = '123123';

  // Derived Financials for Preview
  const financials = getConversionRate();

  useEffect(() => {
    setLocalConfig(PH_CONFIG);
  }, [PH_CONFIG]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (email === ADMIN_EMAIL && password === ADMIN_PASS) {
      setIsAuthenticated(true);
      fetchWithdrawals();
      fetchPlayers();
    } else {
      alert('Invalid Credentials. Access Denied.');
    }
  };

  const handleSaveConfig = () => {
    updateConfig(localConfig);
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    navigate('/');
  };

  const fetchWithdrawals = async () => {
    setLoadingWithdrawals(true);
    try {
      const { data, error } = await supabase
        .from('withdrawals')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (data) setWithdrawals(data);
      else {
        // Fallback for demo
        const local = JSON.parse(localStorage.getItem('redemptions') || '[]');
        setWithdrawals(local.reverse());
      }
    } catch (err) {
      const local = JSON.parse(localStorage.getItem('redemptions') || '[]');
      setWithdrawals(local.reverse());
    } finally {
      setLoadingWithdrawals(false);
    }
  };

  const fetchPlayers = async () => {
    setLoadingPlayers(true);
    try {
      // Query the 'profiles' table which contains ALL registered game players
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (data) setPlayers(data);
    } catch (err) {
      console.error("Error fetching players:", err);
    } finally {
      setLoadingPlayers(false);
    }
  };

  const updateWithdrawalStatus = async (id, newStatus) => {
    setWithdrawals(prev => prev.map(w => w.id === id ? { ...w, status: newStatus } : w));
    try {
      await supabase.from('withdrawals').update({ status: newStatus }).eq('id', id);
    } catch (e) {}
    
    // Sync local storage too
    const local = JSON.parse(localStorage.getItem('redemptions') || '[]');
    const updatedLocal = local.map(w => w.id === id ? { ...w, status: newStatus } : w);
    localStorage.setItem('redemptions', JSON.stringify(updatedLocal));
  };

  const ConfigInput = ({ label, value, field, step = "0.01", suffix = "" }) => (
    <div className="bg-gray-800 p-3 rounded-lg border border-gray-700">
      <label className="block text-gray-400 text-xs uppercase font-bold mb-1">{label}</label>
      <div className="flex items-center">
        <input 
          type="number" 
          step={step} 
          value={value} 
          onChange={(e) => setLocalConfig({ ...localConfig, [field]: parseFloat(e.target.value) })}
          className="w-full bg-transparent text-white font-bold focus:outline-none"
        />
        <span className="text-gray-500 text-sm ml-2">{suffix}</span>
      </div>
    </div>
  );

  const filteredPlayers = players.filter(u => 
    (u.name || '').toLowerCase().includes(playerSearch.toLowerCase()) || 
    (u.email || '').toLowerCase().includes(playerSearch.toLowerCase())
  );

  if (!isAuthenticated) return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-gray-800 p-8 rounded-2xl max-w-sm w-full border border-gray-700 shadow-2xl"
      >
        <div className="text-center mb-6">
          <div className="text-green-500 text-4xl mb-4">
            <SafeIcon icon={FiShield} />
          </div>
          <h2 className="text-2xl font-bold text-white">Admin Access</h2>
          <p className="text-gray-400 text-sm">Authorized Personnel Only</p>
        </div>
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="text-gray-400 text-xs font-bold uppercase">Email</label>
            <input 
              type="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-gray-700 text-white px-4 py-3 rounded-xl border border-gray-600 mt-1 focus:border-green-500 transition-colors focus:outline-none"
              placeholder="admin@admin.com"
            />
          </div>
          <div>
            <label className="text-gray-400 text-xs font-bold uppercase">Password</label>
            <input 
              type="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-gray-700 text-white px-4 py-3 rounded-xl border border-gray-600 mt-1 focus:border-green-500 transition-colors focus:outline-none"
              placeholder="••••••"
            />
          </div>
          <button 
            type="submit" 
            className="w-full bg-green-500 text-white font-bold py-3 rounded-xl hover:bg-green-600 mt-4 shadow-lg transition-transform active:scale-95"
          >
            Login
          </button>
        </form>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 text-white pb-20">
      {/* Navbar */}
      <div className="bg-gray-800 border-b border-gray-700 p-4 sticky top-0 z-50">
        <div className="flex flex-col md:flex-row justify-between items-center max-w-7xl mx-auto gap-4">
          <div className="flex items-center space-x-4 w-full md:w-auto">
            <Link to="/" className="text-gray-400 hover:text-white flex items-center space-x-2 bg-gray-700 px-3 py-1.5 rounded-lg">
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
            </Link>
            <h1 className="text-xl font-bold">Admin Console</h1>
          </div>
          
          <div className="flex bg-gray-700 rounded-lg p-1 w-full md:w-auto overflow-x-auto">
            {['finance', 'payouts', 'players', 'overview'].map(tab => (
              <button 
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 md:flex-none px-4 py-2 rounded-md text-sm font-bold capitalize whitespace-nowrap ${activeTab === tab ? 'bg-green-500 text-white' : 'text-gray-400'}`}
              >
                {tab}
              </button>
            ))}
          </div>
          
          <button onClick={handleLogout} className="absolute top-4 right-4 md:static text-red-400 p-2 hover:bg-gray-700 rounded-lg transition-colors">
            <SafeIcon icon={FiLogOut} />
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 space-y-6">
        
        {/* FINANCE TAB */}
        {activeTab === 'finance' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            {/* Real-time Calculator */}
            <div className="bg-gradient-to-r from-blue-900 to-indigo-900 p-6 rounded-xl border border-blue-500/30 shadow-lg">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <SafeIcon icon={FiTrendingUp} className="text-green-400" /> Live Profit Calculation
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-gray-400 text-xs uppercase font-bold">Rev Per Ad</div>
                  <div className="text-xl md:text-2xl font-bold text-white">₱{financials.devRevenuePerAd.toFixed(4)}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-xs uppercase font-bold">Safe Payout (80%)</div>
                  <div className="text-xl md:text-2xl font-bold text-green-400">₱{financials.safePayoutPerAd.toFixed(4)}</div>
                </div>
                <div className="bg-white/10 rounded-lg p-2 -my-2 border border-white/10 flex flex-col justify-center">
                  <div className="text-gray-300 text-xs uppercase font-bold">Current Rate</div>
                  <div className="text-lg md:text-xl font-bold text-yellow-400">{financials.pointsPerPHP} Pts</div>
                  <div className="text-xs text-gray-400">= ₱1.00</div>
                </div>
                <div>
                  <div className="text-gray-400 text-xs uppercase font-bold">User Earns</div>
                  <div className="text-xl md:text-2xl font-bold text-blue-400">₱{(localConfig.pointsPerAd * financials.valuePerPointPHP).toFixed(4)}</div>
                  <div className="text-xs text-gray-500">per ad</div>
                </div>
              </div>
            </div>

            {/* Config Inputs */}
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <h3 className="font-bold text-lg">Financial Settings</h3>
                <button 
                  onClick={handleSaveConfig}
                  className="w-full md:w-auto bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors"
                >
                  {showSaveSuccess ? <SafeIcon icon={FiCheck} /> : <SafeIcon icon={FiSave} />}
                  {showSaveSuccess ? 'Saved' : 'Save Changes'}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <ConfigInput label="Rewarded Video eCPM (USD)" field="eCPM" value={localConfig.eCPM} suffix="$" />
                <ConfigInput label="Revenue Share (0.0 - 1.0)" field="revenueShare" value={localConfig.revenueShare} suffix="%" />
                <ConfigInput label="USD to PHP Rate" field="exchangeRate" value={localConfig.exchangeRate} suffix="₱" />
                <ConfigInput label="Safety Margin (Payout %)" field="safetyMargin" value={localConfig.safetyMargin} suffix="%" />
                <ConfigInput label="Points Per Ad" field="pointsPerAd" value={localConfig.pointsPerAd} step="1" suffix="pts" />
              </div>
            </div>
          </motion.div>
        )}

        {/* PAYOUTS TAB */}
        {activeTab === 'payouts' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="font-bold">Withdrawal Requests</h3>
              <button onClick={fetchWithdrawals} className="hover:bg-gray-700 p-2 rounded-full transition-colors"><SafeIcon icon={FiRefreshCw} /></button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-gray-750 text-gray-400 uppercase">
                  <tr>
                    <th className="p-4">Date</th>
                    <th className="p-4">User</th>
                    <th className="p-4">Amount</th>
                    <th className="p-4">Status</th>
                    <th className="p-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {withdrawals.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="p-8 text-center text-gray-500">No withdrawal requests found.</td>
                    </tr>
                  ) : (
                    withdrawals.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-750 transition-colors">
                        <td className="p-4">{new Date(item.created_at || item.date).toLocaleDateString()}</td>
                        <td className="p-4">
                          <div className="font-bold text-white">{item.gcash_number}</div>
                          <div className="text-xs text-gray-400">{item.email}</div>
                        </td>
                        <td className="p-4 font-bold text-green-400">₱{item.amount_php}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${item.status === 'paid' ? 'bg-green-500/20 text-green-400' : item.status === 'rejected' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                            {item.status}
                          </span>
                        </td>
                        <td className="p-4 text-right">
                          {item.status === 'pending' && (
                            <div className="flex justify-end gap-2">
                              <button onClick={() => updateWithdrawalStatus(item.id, 'paid')} className="text-green-400 border border-green-400 px-3 py-1 rounded hover:bg-green-400 hover:text-white transition-all font-bold">Pay</button>
                              <button onClick={() => updateWithdrawalStatus(item.id, 'rejected')} className="text-red-400 border border-red-400 px-3 py-1 rounded hover:bg-red-400 hover:text-white transition-all font-bold">Reject</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* PLAYERS TAB (Renamed from Users for clarity) */}
        {activeTab === 'players' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-700 flex flex-col md:flex-row justify-between items-center gap-3">
              <h3 className="font-bold">Active Players ({players.length})</h3>
              <div className="flex gap-2 w-full md:w-auto">
                <div className="relative flex-1 md:flex-initial">
                  <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input 
                    type="text" 
                    placeholder="Search players..." 
                    value={playerSearch}
                    onChange={(e) => setPlayerSearch(e.target.value)}
                    className="w-full bg-gray-900 border border-gray-600 rounded-lg py-2 pl-9 pr-4 text-sm text-white focus:outline-none focus:border-green-500"
                  />
                </div>
                <button onClick={fetchPlayers} className="hover:bg-gray-700 p-2 rounded-lg transition-colors text-white border border-gray-600">
                  <SafeIcon icon={FiRefreshCw} />
                </button>
              </div>
            </div>
            
            {/* Desktop Table / Mobile Cards */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm whitespace-nowrap min-w-[600px]">
                <thead className="bg-gray-750 text-gray-400 uppercase">
                  <tr>
                    <th className="p-4">Player</th>
                    <th className="p-4">Stats</th>
                    <th className="p-4">Referrals</th>
                    <th className="p-4">Joined</th>
                    <th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {filteredPlayers.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="p-8 text-center text-gray-500">
                        {loadingPlayers ? 'Loading players...' : 'No players found.'}
                      </td>
                    </tr>
                  ) : (
                    filteredPlayers.map((player) => (
                      <tr key={player.id} className="hover:bg-gray-750 transition-colors">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-lg">
                              {player.avatar || '🎮'}
                            </div>
                            <div>
                              <div className="font-bold text-white">{player.name}</div>
                              <div className="text-xs text-gray-400">{player.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex flex-col">
                            <span className="text-yellow-400 font-bold">{player.points?.toLocaleString() || 0} pts</span>
                            <span className="text-gray-500 text-xs">Level {player.level || 1} • {player.games_played || 0} Games</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="text-gray-300">
                            <span className="bg-white/10 px-2 py-0.5 rounded text-xs mr-2">{player.referral_code || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="p-4 text-gray-400">
                          {new Date(player.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button className="text-red-400 hover:text-red-300 text-xs border border-red-500/30 px-3 py-1 rounded transition-colors flex items-center gap-1">
                              <SafeIcon icon={FiSlash} className="w-3 h-3" /> Suspend
                            </button>
                            <button className="text-gray-400 hover:text-white text-xs border border-gray-600 px-3 py-1 rounded transition-colors flex items-center gap-1">
                              <SafeIcon icon={FiTrash2} className="w-3 h-3" /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <h3 className="text-gray-400 text-sm font-bold uppercase mb-2">Total Players</h3>
              <div className="text-3xl font-bold text-white flex items-center gap-2">
                <SafeIcon icon={FiUsers} className="text-blue-400" /> 
                {players.length || '--'}
              </div>
              <p className="text-xs text-gray-500 mt-2">Registered Accounts</p>
            </div>
            
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <h3 className="text-gray-400 text-sm font-bold uppercase mb-2">Pending Payouts</h3>
              <div className="text-3xl font-bold text-yellow-400 flex items-center gap-2">
                <SafeIcon icon={FiDollarSign} /> 
                {withdrawals.filter(w => w.status === 'pending').length}
              </div>
            </div>

            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <h3 className="text-gray-400 text-sm font-bold uppercase mb-2">System Status</h3>
              <div className="text-3xl font-bold text-green-400 flex items-center gap-2">
                <SafeIcon icon={FiActivity} /> 
                Online
              </div>
            </div>
          </motion.div>
        )}
        
      </div>
    </div>
  );
};

export default AdminDashboard;